import React, { Fragment, forwardRef, useMemo, type PropsWithChildren, type Ref } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts/ui/Spotlight';
import { SpotlightManager } from '@atlassiansox/engagekit-ts/spotlight-manager';
import { SpotlightTarget } from '@atlassiansox/engagekit-ts/spotlight-target';

import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import DeprecatedSpotlightTarget from '@atlaskit/onboarding/spotlight-target'; // eslint-disable-line @atlaskit/design-system/use-spotlight-package
import { AppSwitcher as AkAppSwitcherNav4 } from '@atlaskit/navigation-system/top-nav-items';
import { SwitchToTooltipText } from '@atlassian/switcher/switch-to-tooltip-text';
import {
	createUnitTileAppSwitcherIcon,
	type UnitProfile,
} from '@atlassian/switcher/unit-tile-app-switcher-icon';
import { fg } from '@atlassian/jira-feature-gating';

type Props = {
	onOpen?(): void;
	onMouseEnter?(): void;
	// NAVX-5411: spike measuring the cost of fetching + rendering a unit tile
	// as the app switcher trigger icon. Null when the experiment is off.
	unitProfile: UnitProfile | null;
};

/**
 * AppSwitcherPlaceholderButton is designed to only show until the user has clicked it.
 * Then, it should hide itself forever.
 *
 * Motivation: This button acts as a placeholder for when the actual AppSwitcher
 * is loaded. We don't want to load the actual AppSwitcher until it's needed,
 * as it is large. So, the parent component shows this component until such time
 * as the user clicks on it. Then, the real AppSwitcher component is loaded, and
 * replaces this one.
 */
export const AppSwitcherPlaceholderButton: React.ForwardRefExoticComponent<
	Props & React.RefAttributes<HTMLButtonElement>
> = forwardRef(AppSwitcherPlaceholderButtonWithRef);

function AppSwitcherPlaceholderButtonWithRef(
	{ onOpen, onMouseEnter, unitProfile }: Props,
	ref: Ref<HTMLButtonElement>,
) {
	// NAVX-5411: swap in a platform-owned unit tile icon for the app switcher
	// trigger when a unit profile is available for the current site. Memoized
	// so the icon component type stays stable across renders and AkAppSwitcherNav4
	// doesn't unmount/remount it unnecessarily.
	const icon = useMemo(
		() => (unitProfile ? createUnitTileAppSwitcherIcon(unitProfile) : undefined),
		[unitProfile],
	);

	return (
		<AppSwitcherLightWrapper>
			<AkAppSwitcherNav4
				ref={ref}
				onClick={onOpen}
				onMouseEnter={onMouseEnter}
				label={SwitchToTooltipText}
				icon={icon}
				aria-expanded={false}
				aria-haspopup
				interactionName="jira_app_switcher"
			/>
		</AppSwitcherLightWrapper>
	);
}

const AppSwitcherLightWrapper = componentWithFG(
	'app_switcher_spotlight_gate',
	SpotLightWrapper,
	Fragment,
);

function SpotLightWrapper(props: PropsWithChildren) {
	return (
		<SpotlightManager>
			<div id="app-switcher-confluence-ap-ads-spotlight">
				{fg('twcg-2261-ap-spotlight-migration') ? (
					<SpotlightTarget name="app-switcher-spotlight">{props.children}</SpotlightTarget>
				) : (
					<DeprecatedSpotlightTarget name="app-switcher-spotlight">
						{props.children}
					</DeprecatedSpotlightTarget>
				)}
			</div>
			<EngagementSpotlight isMigrated engagementId="app-switcher-spotlight" />
		</SpotlightManager>
	);
}
